import React, { useState, useEffect } from "react";
import axios from "axios";
import Header from "../components/Header";
import Footer from "../components/Footer";
import Statistics from "../components/Statistics";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

const toAbsUrl = (url) => {
  if (!url) return null;
  if (/^https?:\/\//i.test(url)) return url;
  const base = (API_BASE_URL || "").replace(/\/api\/?$/, "");
  return `${base}${url.startsWith("/") ? url : `/${url}`}`;
};

// Convert Google Maps embed URL to a regular viewable URL
const convertEmbedToViewUrl = (embedUrl) => {
  if (!embedUrl) return "https://maps.google.com";

  // If it's already a regular Google Maps URL (not an embed), return as-is
  if (!embedUrl.includes("/maps/embed")) {
    return embedUrl;
  }

  try {
    const url = new URL(embedUrl);

    // Check if there's a 'q' parameter (location query)
    const qParam = url.searchParams.get("q");
    if (qParam) {
      return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
        qParam
      )}`;
    }

    // Check for 'pb' parameter and extract location
    const pbParam = url.searchParams.get("pb");
    if (pbParam) {
      // First, try to extract place name (after !2s marker)
      // Example: !2sSilicon%20Towers%2C%20Image%20Gardens%20Road
      const placeMatch = pbParam.match(/!2s([^!]+)/);
      if (placeMatch && placeMatch[1]) {
        const placeName = decodeURIComponent(placeMatch[1]);
        return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
          placeName
        )}`;
      }

      // If no place name, try to extract coordinates from pb parameter
      // pb parameter format: !3d17.44956 (latitude) !2d78.38547 (longitude)
      const latMatch = pbParam.match(/!3d([-\d.]+)/);
      const lngMatch = pbParam.match(/!2d([-\d.]+)/); // Changed from !4d to !2d

      if (latMatch && lngMatch) {
        const lat = latMatch[1];
        const lng = lngMatch[1];
        return `https://www.google.com/maps/search/?api=1&query=${lat},${lng}`;
      }
    }

    // If we can't extract location, return the URL with /embed removed
    return embedUrl.replace("/maps/embed", "/maps");
  } catch (e) {
    console.error("Error converting embed URL:", e);
    return embedUrl;
  }
};

const ContactUs = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    mobile: "",
    message: "",
  });
  const [loading, setLoading] = useState(false);
  const [heroData, setHeroData] = useState({});
  const [enquiryData, setEnquiryData] = useState({});
  const [officeData, setOfficeData] = useState({});
  const [contentLoading, setContentLoading] = useState(true);

  useEffect(() => {
    let mounted = true;

    const fetchAll = async () => {
      try {
        const [heroRes, eqRes, officeRes] = await Promise.allSettled([
          axios.get(`${API_BASE_URL}/contact/hero?ts=${Date.now()}`),
          axios.get(`${API_BASE_URL}/contact/enquiry-form?ts=${Date.now()}`),
          axios.get(`${API_BASE_URL}/contact/office?ts=${Date.now()}`),
        ]);

        const hero =
          heroRes.status === "fulfilled"
            ? heroRes.value.data?.[0] || heroRes.value.data || {}
            : {};

        const eq =
          eqRes.status === "fulfilled"
            ? eqRes.value.data?.[0] || eqRes.value.data || {}
            : {};

        let officeRaw = {};
        if (officeRes.status === "fulfilled") {
          const d = officeRes.value.data;
          officeRaw = Array.isArray(d) ? d[0] || {} : d || {};
        }

        if (mounted) {
          setHeroData({
            title: hero.title || "",
            subtitle: hero.subtitle || "",
            description: hero.description || "",
            image: hero.image || "",
          });

          setEnquiryData({
            title: eq.title || "",
            subtitle: eq.subtitle || "",
            description: eq.description || "",
            phone_number: eq.phone_number || "",
            email: eq.email || "",
          });

          setOfficeData({
            title: officeRaw.office_title || "Office & Location",
            description: officeRaw.office_description || "",
            office_time: officeRaw.office_hours || "",
            address: officeRaw.address || "", // ✅ fixed
            map_embed: officeRaw.office_map || "",
            map_link: officeRaw.office_map || "",
          });

          setContentLoading(false);
        }
      } catch (err) {
        console.error("Error loading contact content:", err);
        if (mounted) setContentLoading(false);
      }
    };

    fetchAll();
    return () => {
      mounted = false;
    };
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const validateEmail = (email) => {
    const regex = /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/;
    return regex.test(String(email).trim());
  };

  const validateMobile = (mobile) => {
    const regex = /^[0-9]{10}$/;
    return regex.test(mobile);
  };

 const handleSubmit = async (e) => {
  e.preventDefault();

  if (!validateEmail(formData.email)) {
    alert("Please enter a valid email address.");
    return;
  }

  if (!validateMobile(formData.mobile)) {
    alert("Please enter a valid 10 digits mobile number ");
    return;
  }

  setLoading(true);

 const erpPayload = {
  Title: "Mr",                     // required by backend
  clientname: formData.name,
  mobilenumber: formData.mobile,
  email: formData.email,
  location: "",
  customercomments: formData.message,
  projectname: "Shades of Green - Marketing Office",   // required by backend
};


  try {
    const results = await Promise.allSettled([
      axios.post(`${API_BASE_URL}/home/contact`, formData),
      axios.post(`${API_BASE_URL}/erp/submit-lead`, erpPayload),
    ]);

    const websiteOk = results[0].status === "fulfilled";
    const erpOk = results[1].status === "fulfilled";

    if (!websiteOk) {
      console.error("❌ Website form failed:", results[0].reason);
      alert("Something went wrong while sending your message.");
      return;
    }

    if (!erpOk) {
      console.error("⚠️ ERP push failed:", results[1].reason);
    }

    alert("Thank you for reaching out! We’ll get back to you within 24 hours.");
    console.log("✅ Contact form submitted successfully");
    setFormData({ name: "", email: "", mobile: "", message: "" });

  } catch (err) {
    console.error("❌ Submission failed:", err);
    alert("Something went wrong while sending your message.");
  } finally {
    setLoading(false);
  }
};


  if (contentLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 text-gray-500 text-base sm:text-lg">
        Loading content…
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <Header />

      {/* Hero Section */}
      <section className="relative pt-24 sm:pt-28 md:pt-32 lg:pt-36 py-10 sm:py-14 md:py-20 px-4 sm:px-6 md:px-8 bg-gradient-to-br from-gray-50 to-white">
        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-10 sm:gap-12 md:gap-16 items-center">
          <div className="space-y-4 sm:space-y-5 md:space-y-6 order-2 lg:order-1 text-left">
            {heroData.title && (
              <div className="inline-block px-4 sm:px-5 py-2 bg-blue-100 text-blue-600 text-xs sm:text-sm font-semibold rounded-full">
                {heroData.title}
              </div>
            )}
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 leading-tight break-words">
              {heroData.subtitle || (
                <>
                  Contact <span className="text-blue-600">HPR</span>
                  <br />
                  <span className="text-teal-600">Infra</span>
                </>
              )}
            </h1>
            <p className="text-sm sm:text-base md:text-lg text-gray-600 leading-relaxed max-w-lg text-justify">
              {heroData.description ||
                "Talk to us about site visits, bookings, documentation, or partnerships. We keep it simple and transparent."}
            </p>
            <a
              href="#contact-form"
              className="inline-flex items-center justify-center bg-blue-600 text-white px-6 sm:px-8 py-3 sm:py-4 rounded-lg font-semibold sm:font-bold text-sm sm:text-lg hover:bg-blue-700 transition-all shadow-md sm:shadow-lg"
            >
              Contact Us
              <svg
                className="w-4 h-4 sm:w-5 sm:h-5 ml-2"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M13 7l5 5m0 0l-5 5m5-5H6"
                />
              </svg>
            </a>
          </div>

          <div className="order-1 lg:order-2">
            <img
              src={
                toAbsUrl(heroData.image) ||
                "https://images.unsplash.com/photo-1497366811353-6870744d04b2?auto=format&fit=crop&w=1200&q=80"
              }
              alt="Contact hero"
              loading="lazy"
              className="w-full aspect-[4/3] object-cover rounded-2xl shadow-xl"
            />
          </div>
        </div>
      </section>

      <Statistics />

      {/* Contact Section */}
      <section
        id="contact-form"
        className="pt-16 sm:pt-20 md:pt-24 py-10 sm:py-14 md:py-16 px-4 sm:px-6 md:px-8 bg-gray-50"
      >
        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-10 sm:gap-12 md:gap-16">
          <div className="space-y-5 sm:space-y-6 md:space-y-8 text-left">
            {enquiryData.title && (
              <div className="inline-block px-4 sm:px-5 py-2 bg-blue-100 text-blue-600 text-xs sm:text-sm font-semibold rounded-full">
                {enquiryData.title}
              </div>
            )}
            <h2 className="text-2xl sm:text-3xl md:4xl font-bold text-gray-900 leading-tight">
              {enquiryData.subtitle || "Enquiry Form"}
            </h2>
            <p className="text-sm sm:text-base md:text-lg text-gray-600 leading-relaxed">
              {enquiryData.description ||
                "We'll align on location, unit or plot type, budgets, and timelines, then plan a walkthrough at your preferred project."}
            </p>

            <div className="space-y-4 mt-4 sm:mt-6">
              {enquiryData.phone_number && (
                <div className="bg-blue-50 rounded-2xl p-4 sm:p-5 flex items-start gap-3 sm:gap-4 hover:shadow-md transition">
                  <div className="bg-blue-600 rounded-xl p-3 sm:p-4">
                    <svg
                      className="w-5 h-5 sm:w-6 sm:h-6 text-white"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
                      />
                    </svg>
                  </div>
                  <div>
                    <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-1">
                      Phone
                    </h3>
                    <p className="text-sm sm:text-base md:text-lg text-gray-700">
                      {enquiryData.phone_number}
                    </p>
                  </div>
                </div>
              )}

              {enquiryData.email && (
                <div className="bg-green-50 rounded-2xl p-4 sm:p-5 flex items-start gap-3 sm:gap-4 hover:shadow-md transition">
                  <div className="bg-green-600 rounded-xl p-3 sm:p-4">
                    <svg
                      className="w-5 h-5 sm:w-6 sm:h-6 text-white"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                      />
                    </svg>
                  </div>
                  <div>
                    <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-1">
                      Email
                    </h3>
                    <p className="text-sm sm:text-base md:text-lg text-gray-700">
                      {enquiryData.email}
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="bg-white rounded-2xl p-6 sm:p-8 md:p-10 shadow-lg sm:shadow-xl border border-gray-100">
            <h3 className="text-lg sm:text-xl md:text-2xl font-bold text-gray-800 mb-5 sm:mb-6">
              Send us a message
            </h3>
            <form onSubmit={handleSubmit} className="space-y-3 sm:space-y-4">
              <input
                type="text"
                name="name"
                placeholder="Full Name"
                value={formData.name}
                onChange={handleChange}
                required
                className="w-full p-2.5 sm:p-3 border border-gray-200 rounded-lg focus:border-blue-500 outline-none text-sm sm:text-base"
              />
              <input
                type="tel"
                name="mobile"
                placeholder="Mobile Number"
                value={formData.mobile}
                onChange={handleChange}
                required
                className="w-full p-2.5 sm:p-3 border border-gray-200 rounded-lg focus:border-blue-500 outline-none text-sm sm:text-base"
              />
              <input
                type="email"
                name="email"
                placeholder="Email"
                value={formData.email}
                onChange={handleChange}
                required
                className="w-full p-2.5 sm:p-3 border border-gray-200 rounded-lg focus:border-blue-500 outline-none text-sm sm:text-base"
              />
              <textarea
                name="message"
                placeholder="Your Message"
                value={formData.message}
                onChange={handleChange}
                rows={4}
                className="w-full p-2.5 sm:p-3 border border-gray-200 rounded-lg focus:border-blue-500 outline-none resize-none text-sm sm:text-base"
              />
              <button
                type="submit"
                disabled={loading}
                className={`w-full ${
                  loading
                    ? "bg-gray-400 cursor-not-allowed"
                    : "bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700"
                } text-white py-2.5 sm:py-3 rounded-lg font-semibold text-sm sm:text-lg shadow-md transition-all`}
              >
                {loading ? "Sending..." : "Send Message"}
              </button>
            </form>
          </div>
        </div>
      </section>

      {/* Office Section */}
      <section
        id="find-us"
        className="pt-20 sm:pt-24 md:pt-28 pb-16 px-4 bg-gray-50"
      >
        <div className="container mx-auto max-w-7xl grid grid-cols-1 lg:grid-cols-2 gap-8">
          <div className="bg-white rounded-2xl overflow-hidden shadow-xl border border-gray-200 h-96">
            <iframe
              src={officeData.map_embed || ""}
              width="100%"
              height="100%"
              style={{ border: 0 }}
              allowFullScreen=""
              loading="lazy"
              title={officeData.title || "Office Location"}
            ></iframe>
          </div>

          <div className="bg-white rounded-2xl p-8 shadow-xl border border-gray-200">
            <h3 className="text-2xl font-bold text-gray-800 mb-6">
              {officeData.title || "Our Office"}
            </h3>
            <p className="text-gray-600 mb-4 leading-relaxed whitespace-pre-line">
              {officeData.description ||
                "Visit our head office for project discussions and consultations."}
            </p>
            {officeData.office_time && (
              <p className="text-gray-600 mb-3">
                <strong>Hours:</strong> {officeData.office_time}
              </p>
            )}
            {officeData.address && (
              <p className="text-gray-600 mb-4 whitespace-pre-line">
                {officeData.address}
              </p>
            )}
            <a
              href={convertEmbedToViewUrl(officeData.map_link)}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center mt-4 text-blue-600 hover:text-blue-700 font-semibold"
            >
              Get Directions
              <svg
                className="w-5 h-5 ml-2"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M13 7l5 5m0 0l-5 5m5-5H6"
                />
              </svg>
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ContactUs;
